import React, { forwardRef } from 'react';
import {
  Box,
  Typography,
  CardContent,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  AccessTime,
  Schedule
} from '@mui/icons-material';
import { GlassCard, GlassChip } from './GlassComponents';

// Glass Page Header - For page headers with title, subtitle and action buttons
export const GlassPageHeader = forwardRef(({
  title,
  subtitle,
  action,
  ...props
}, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      ref={ref}
      sx={{
        position: 'relative',
        mb: isMobile ? 3 : 4,
        ...props.sx,
      }}
      {...props}
    >
      <Box>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            color: 'white',
            fontWeight: 600,
            fontSize: isMobile ? '1.8rem' : '2.5rem',
            mb: 1
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: isMobile ? '0.9rem' : '1.1rem'
          }}
        >
          {subtitle}
        </Typography>
      </Box>
      {action && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          {action}
        </Box>
      )}
    </Box>
  );
});

// Glass Profile Card - Reusable card for displaying profile information
export const GlassProfileCard = forwardRef(({
  profile,
  loading,
  onClick,
  avatarComponent,
  showTimestamps = true,
  ...props
}, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const getTimeSince = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const now = new Date();
    const then = new Date(timestamp);
    const diffDays = Math.floor((now - then) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp).toLocaleDateString();
  };

  return (
    <GlassCard
      ref={ref}
      className="profile-card"
      onClick={() => onClick(profile)}
      variant={profile.variant || 'default'}
      clickable
      disabled={loading}
      {...props}
    >
      <CardContent sx={{
        textAlign: 'center',
        p: isMobile ? 2.5 : 3,
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}>
        <Box>
          {profile.avatarComponent || avatarComponent || (
            <Box sx={{
              mx: 'auto',
              mb: 2,
              width: isMobile ? 70 : 80,
              height: isMobile ? 70 : 80,
              border: '2px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '50%',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            }}>
              {profile.name?.charAt(0)?.toUpperCase()}
            </Box>
          )}

          <Typography
            variant="h6"
            component="h2"
            gutterBottom
            sx={{
              color: 'white',
              fontWeight: 500,
              fontSize: isMobile ? '1.1rem' : '1.25rem'
            }}
          >
            {profile.name}
          </Typography>

          {profile.badges && profile.badges.map((badge, index) => (
            <GlassChip
              key={index}
              variant={badge.variant}
              label={badge.label}
              icon={badge.icon}
              size="small"
              sx={{ mt: 0, mb: 1, mr: 0.5 }}
            />
          ))}
        </Box>

        <Box>
          {profile.statusBadges && profile.statusBadges.map((badge, index) => (
            <GlassChip
              key={index}
              variant={badge.variant}
              label={badge.label}
              size="small"
              sx={{ mt: 0, mb: 1, mr: 0.5 }}
            />
          ))}
        </Box>

        {showTimestamps && profile.timestamps && profile.timestamps.lastSeen && (
          <Box>
            {profile.timestamps.lastSeen && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                <AccessTime sx={{ fontSize: 14, mr: 0.5, color: 'rgba(255, 255, 255, 0.5)' }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: isMobile ? '0.7rem' : '0.75rem'
                  }}
                >
                  Last seen: {getTimeSince(profile.timestamps.lastSeen)}
                </Typography>
              </Box>
            )}

            {profile.timestamps.firstUsed && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0 }}>
                <Schedule sx={{ fontSize: 14, mr: 0.5, color: 'rgba(255, 255, 255, 0.5)' }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: isMobile ? '0.7rem' : '0.75rem'
                  }}
                >
                  First used: {formatDate(profile.timestamps.firstUsed)}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </GlassCard>
  );
});

// Glass Profile Grid - Grid container for profile cards
export const GlassProfileGrid = forwardRef(({
  profiles,
  loading,
  onProfileClick,
  profileCardProps,
  gridItemProps,
  ...props
}, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Grid container spacing={isMobile ? 2 : 3} ref={ref} {...props}>
      {profiles.map((profile) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={profile.id} {...gridItemProps}>
          <GlassProfileCard
            profile={profile}
            loading={loading}
            onClick={onProfileClick}
            {...profileCardProps}
          />
        </Grid>
      ))}
    </Grid>
  );
});

// Glass Status Text - For status and loading states
export const GlassStatusText = forwardRef(({ children, variant = 'loading', ...props }, ref) => {
  const variants = {
    loading: {
      color: 'rgba(255, 255, 255, 0.7)',
    },
    empty: {
      color: 'rgba(255, 255, 255, 0.6)',
    },
    error: {
      color: 'rgba(255, 107, 107, 0.9)',
    },
  };

  return (
    <Box sx={{ textAlign: 'center', mt: 4 }} ref={ref} {...props}>
      <Typography
        variant="body1"
        sx={variants[variant]}
      >
        {children}
      </Typography>
    </Box>
  );
});

const GlassLayoutComponentsExport = {
  GlassPageHeader,
  GlassProfileCard,
  GlassProfileGrid,
  GlassStatusText,
};

export default GlassLayoutComponentsExport;